import { useState, useEffect } from "react";
import { supabase } from "../config/supabaseClient";
import { Table, Button, Modal, Input, message, Spin, Space } from "antd";
import { EditOutlined, DeleteOutlined } from "@ant-design/icons";

const Results = () => {
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [studentName, setStudentName] = useState("");
  const [subject, setSubject] = useState("");
  const [marks, setMarks] = useState("");
  const [grade, setGrade] = useState("");
  const [studentEmail, setStudentEmail] = useState("");
  const [editingResult, setEditingResult] = useState(null);

  const fetchResults = async () => {
    const { data, error } = await supabase
      .from("results")
      .select("*")
      .order("created_at", { ascending: false });
    if (!error) {
      setResults(data);
    }
    setLoading(false);
  };

  useEffect(() => {
    fetchResults();

    const channel = supabase
      .channel("results-changes")
      .on(
        "postgres_changes",
        { event: "*", schema: "public", table: "results" },
        () => fetchResults()
      )
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  }, []);

  // Check if a result for the student email already exists.
  const checkIfResultExists = async (email) => {
    const { count, error } = await supabase
      .from("results")
      .select("id", { count: "exact", head: true })
      .eq("student_email", email);
    if (error) return false;
    return count > 0;
  };

  const resolveStudentId = async (email) => {
    const { data } = await supabase
      .from("profiles")
      .select("id")
      .eq("email", email)
      .eq("type", "student")
      .single();
    return data?.id || null;
  };

  const handleUpload = async () => {
    if (!studentName || !subject || !marks || !grade || !studentEmail) {
      message.error("Please fill all fields: student name, subject, marks, grade, and email!");
      return;
    }

    try {
      const resultExists = await checkIfResultExists(studentEmail);
      if (resultExists) {
        message.error("Results already added for this email.");
        return;
      }

      const studentId = await resolveStudentId(studentEmail);

      const { error } = await supabase.from("results").insert({
        student_id: studentId,
        student_name: studentName,
        subject,
        marks,
        grade,
        student_email: studentEmail,
      });

      if (error) {
        message.error("Error adding result: " + error.message);
        return;
      }

      message.success("Result added successfully!");
      resetModalState();
    } catch (error) {
      message.error("Error adding result: " + error.message);
    }
  };

  const openEditModal = (result) => {
    setEditingResult(result);
    setStudentName(result.student_name);
    setSubject(result.subject);
    setMarks(result.marks);
    setGrade(result.grade);
    setStudentEmail(result.student_email);
    setIsModalVisible(true);
  };

  const handleEditSave = async () => {
    if (!studentName.trim() || !subject.trim() || !marks.trim() || !grade.trim() || !studentEmail.trim()) {
      message.error("All fields must be filled!");
      return;
    }

    try {
      const { error } = await supabase
        .from("results")
        .update({
          student_name: studentName,
          subject,
          marks,
          grade,
          student_email: studentEmail,
        })
        .eq("id", editingResult.id);

      if (error) {
        message.error("Error updating result: " + error.message);
        return;
      }

      message.success("Result updated successfully!");
      resetModalState();
    } catch (error) {
      message.error("Error updating result: " + error.message);
    }
  };

  const handleDelete = async (resultId) => {
    try {
      const { error } = await supabase.from("results").delete().eq("id", resultId);
      if (error) {
        message.error("Error deleting result: " + error.message);
        return;
      }
      message.success("Result deleted successfully!");
    } catch (error) {
      message.error("Error deleting result: " + error.message);
    }
  };

  const resetModalState = () => {
    setIsModalVisible(false);
    setEditingResult(null);
    setStudentName("");
    setSubject("");
    setMarks("");
    setGrade("");
    setStudentEmail("");
  };

  const columns = [
    { title: "Student Name", dataIndex: "student_name", key: "student_name" },
    { title: "Subject", dataIndex: "subject", key: "subject" },
    { title: "Marks", dataIndex: "marks", key: "marks" },
    { title: "Grade", dataIndex: "grade", key: "grade" },
    {
      title: "Actions",
      key: "actions",
      render: (_, record) => (
        <Space>
          <Button
            type="text"
            icon={<EditOutlined />}
            onClick={() => openEditModal(record)}
          />
          <Button
            type="text"
            icon={<DeleteOutlined />}
            danger
            onClick={() => handleDelete(record.id)}
          />
        </Space>
      ),
    },
  ];

  return (
    <div className="container mx-auto md:p-2 py-10">
      <div className="flex justify-between items-center mb-4 bg-gray-900 py-3 px-4 rounded-lg">
        <h1 className="text-2xl text-white font-semibold">Results Management</h1>
        <Button
          className="bg-green-600 font-semibold text-white hover:bg-green-700"
          type="none"
          onClick={() => setIsModalVisible(true)}
        >
          Add Result
        </Button>
      </div>

      <Spin spinning={loading} tip="Loading Results...">
        <div className="overflow-x-auto">
          <Table
            dataSource={results}
            columns={columns}
            rowKey="id"
            pagination={{ pageSize: 6 }}
            className="mt-4"
            scroll={{ x: "max-content" }}
          />
        </div>
      </Spin>

      <Modal
        title={editingResult ? "Edit Result" : "Add Result"}
        visible={isModalVisible}
        onCancel={resetModalState}
        onOk={editingResult ? handleEditSave : handleUpload}
        okText={editingResult ? "Save Changes" : "Add Result"}
        width={600}
      >
        <div className="space-y-4">
          <Input
            placeholder="Student Name"
            value={studentName}
            onChange={(e) => setStudentName(e.target.value)}
          />
          <Input
            placeholder="Subject"
            value={subject}
            onChange={(e) => setSubject(e.target.value)}
          />
          <Input
            placeholder="Marks"
            value={marks}
            onChange={(e) => setMarks(e.target.value)}
          />
          <Input
            placeholder="Grade"
            value={grade}
            onChange={(e) => setGrade(e.target.value)}
          />
          <Input
            placeholder="Student Email"
            value={studentEmail}
            onChange={(e) => setStudentEmail(e.target.value)}
          />
        </div>
      </Modal>
    </div>
  );
};

export default Results;
